import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import React, { startTransition, useCallback, useState } from "react";
import { cn } from "../../helpers/classNames";
import { getLinkPartsFromSlug } from "../../helpers/getLinkPartsFromSlug";
import { getRoute } from "../../helpers/urls/getRoute";
import { useCountry } from "../../hooks/useCountry";
import { usePageChanged } from "../../hooks/useIsPageChange";
import { useIsPersistingForbidden } from "../../hooks/useIsPersistingForbidden";
import { useLocateMe } from "../../hooks/useLocateMe";
import { usePageParams } from "../../hooks/usePageParams";
import { Button } from "../Button";
import { ItemLoading } from "./itemLoading";
import styles from "./suggestList.module.scss";

type Props = {
    onItemClick: () => void;
    suggestionClicked: boolean;
};
export const ItemLocateMe: React.FC<Props> = ({
    onItemClick,
    suggestionClicked,
}) => {
    const country = useCountry();
    const pageParams = usePageParams();
    const router = useRouter();
    const isPersistingForbidden = useIsPersistingForbidden();
    const t = useTranslations("component.autoSuggest");
    const errorMessage = t("locateMe.errorMessage");
    const [label, setLabel] = useState(t("locateMe.defaultLabel"));
    const {
        locateMe,
        permissionDenied,
        currentLocation,
        isLoading: isLocatingMe,
    } = useLocateMe();
    const [isLoading, setIsLoading] = useState(isLocatingMe);
    const { pageType } = pageParams;

    usePageChanged(() => {
        setIsLoading(false);
        onItemClick();
    });

    const geoLocate = useCallback(
        async (forceRelocate: boolean): Promise<void> => {
            startTransition(() => setIsLoading(true));
            return locateMe(forceRelocate)
                .then((posData) => {
                    if (posData?.location?.seoString) {
                        const { code, seoString } = getLinkPartsFromSlug(
                            posData.location.seoString
                        );
                        if (
                            pageType?.isForecastTodayPage &&
                            pageParams.code === code
                        ) {
                            startTransition(() => setIsLoading(false));
                            onItemClick();
                        } else {
                            startTransition(() =>
                                router.push(
                                    getRoute({
                                        parameters: {
                                            pageName: "forecastToday",
                                            seoString,
                                            code,
                                        },
                                        country,
                                    })
                                )
                            );
                        }
                    } else {
                        startTransition(() => setLabel(t("locateMe.tryAgain")));
                    }
                })
                .catch(() => {});
        },
        [
            country,
            locateMe,
            onItemClick,
            pageParams.code,
            pageType?.isForecastTodayPage,
            router,
            t,
        ]
    );

    return (
        <ul className={styles.group} key="locate-me-group">
            <li className={styles.title} data-testid="myLocation">
                <span className="icon-crosshairs" />
                {t("locateMe.myLocation")}
            </li>
            <li className={styles.item} key="locate-me-item">
                {isPersistingForbidden || permissionDenied ? (
                    <div className={styles.itemInner}>
                        <Button
                            variant="unstyled"
                            className={styles.itemError}
                            onClick={(e) => e.preventDefault()}
                        >
                            {errorMessage}
                        </Button>
                    </div>
                ) : isLoading ? (
                    <ItemLoading />
                ) : currentLocation ? (
                    <div className={styles.itemInner}>
                        <Button
                            variant="unstyled"
                            className={cn(
                                styles.itemInnerInner,
                                suggestionClicked && styles.inactive
                            )}
                            onClick={() =>
                                !suggestionClicked && void geoLocate(false)
                            }
                        >
                            {currentLocation.location?.cityName}
                        </Button>
                        <Button
                            variant="unstyled"
                            className={cn(
                                styles.itemInnerInner,
                                styles.itemInnerInnerRelocate,
                                suggestionClicked && styles.inactive
                            )}
                            onClick={() =>
                                !suggestionClicked && void geoLocate(true)
                            }
                            data-testid="relocate"
                        >
                            {t("locateMe.relocate")}
                            <span className="icon-reload" />
                        </Button>
                    </div>
                ) : (
                    <div className={styles.itemInner}>
                        <Button
                            variant="unstyled"
                            className={cn(
                                styles.itemInnerInner,
                                suggestionClicked && styles.inactive
                            )}
                            onClick={() =>
                                !suggestionClicked && void geoLocate(false)
                            }
                            data-testid="defaultLabel"
                        >
                            {label}
                        </Button>
                    </div>
                )}
            </li>
        </ul>
    );
};
